import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import UserHeader from "@/components/user/UserHeader";
import Footer from "@/components/layout/Footer";
import { useAuth } from '@/hooks/useAuth.tsx';
import { Home, ArrowLeft } from 'lucide-react';

// Componente para exibir a imagem com fallback adequado
const ProjectImage = ({ src, alt }) => {
  const [hasError, setHasError] = useState(false);
  
  const handleError = () => {
    setHasError(true);
  };
  
  if (hasError) {
    return (
      <div className="w-full h-full bg-gradient-to-r from-blue-200 to-cyan-300 flex items-center justify-center">
        <span className="text-lg font-semibold text-gray-700">{alt}</span>
      </div>
    );
  }
  
  return (
    <img 
      src={src || "/lovable-uploads/default-project.jpg"}
      alt={alt}
      className="w-full h-full object-cover"
      onError={handleError}
    />
  );
};

const MyProjects: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeFilter, setActiveFilter] = useState('todos');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  
  // Filtros selecionados
  const [filtroTipo, setFiltroTipo] = useState<string | null>(null);
  const [filtroStatus, setFiltroStatus] = useState<string | null>(null);
  const [filtroRegiao, setFiltroRegiao] = useState<string | null>(null);
  const [filtroOds, setFiltroOds] = useState<string | null>(null);
  
  // Mock de projetos do usuário
  const userProjects = [
    {
      id: 10,
      name: "Moeda Social Carbono",
      type: "Mitigação",
      region: "Nacional",
      status: "Em andamento",
      description: "Sistema inovador de moeda social baseada em créditos de carbono, permitindo que comunidades locais participem ativamente da economia verde através do incentivo a práticas sustentáveis.",
      image: "/lovable-uploads/7a72b832-cca8-4f76-b696-877484eef2cd.png"
    },
    {
      id: 11,
      name: "Plante para Neutralizar",
      type: "Conservação",
      region: "Sudeste",
      status: "Em andamento",
      description: "Iniciativa de preservação do Manguezal do Jequiá, com foco na valorização do carbono azul, sequestro de carbono e geração de renda para comunidades locais.",
      image: "/images/projects/plante-para-neutralizar.jpg"
    }
  ];

  // Função para alterar o filtro ativo
  const handleFilterTabChange = (filter: string) => {
    setActiveFilter(filter);
    setOpenDropdown(null); // Fecha o dropdown ao mudar de aba
  };

  // Função para alternar o dropdown aberto
  const toggleDropdown = (dropdown: string) => {
    if (openDropdown === dropdown) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(dropdown);
    }
  };

  // Função para aplicar filtro de tipo
  const aplicarFiltroTipo = (tipo: string) => {
    setFiltroTipo(tipo);
    setOpenDropdown(null);
  };

  // Função para aplicar filtro de status
  const aplicarFiltroStatus = (status: string) => {
    setFiltroStatus(status);
    setOpenDropdown(null);
  };

  // Função para aplicar filtro de região
  const aplicarFiltroRegiao = (regiao: string) => {
    setFiltroRegiao(regiao);
    setOpenDropdown(null);
  };

  // Função para aplicar filtro de ODS
  const aplicarFiltroOds = (ods: string) => {
    setFiltroOds(ods);
    setOpenDropdown(null);
  };

  // Limpar todos os filtros
  const limparFiltros = () => {
    setFiltroTipo(null);
    setFiltroStatus(null);
    setFiltroRegiao(null);
    setFiltroOds(null);
  };

  // Filtros organizados por categoria
  const filterOptions = {
    tipos: ["Reflorestamento", "Manejo de Resíduos"],
    status: ["Em implementação", "Finalizado"],
    regioes: ["Sudeste", "Sul"],
    ods: ["ODS 13: Ação Climática", "ODS 15: Vida Terrestre", "ODS 12: Consumo Responsável"]
  };

  // Filtrar projetos com base nos filtros selecionados
  const projetosFiltrados = userProjects.filter(project => {
    // Se nenhum filtro estiver ativo ou o filtro "todos" estiver selecionado
    if (activeFilter === 'todos' && !filtroTipo && !filtroStatus && !filtroRegiao && !filtroOds) {
      return true;
    }

    // Filtro por tipo
    if (filtroTipo && project.type !== filtroTipo) {
      return false;
    }

    // Filtro por status
    if (filtroStatus && project.status !== filtroStatus) {
      return false;
    }

    // Filtro por região
    if (filtroRegiao && project.region !== filtroRegiao) {
      return false;
    }

    // Filtro por ODS (simulado - em produção real, os projetos teriam um array de ODS)
    if (filtroOds) {
      // Aqui estamos simplificando: em um sistema real, você teria uma relação entre projetos e ODS
      const odsSimulado: { [key: string]: string[] } = {
        "Reflorestamento": ["ODS 13: Ação Climática", "ODS 15: Vida Terrestre"],
        "Manejo de Resíduos": ["ODS 11: Cidades Sustentáveis", "ODS 12: Consumo Responsável"]
      };
      
      // Verifica se o projeto corresponde ao ODS selecionado
      const projetoOds = odsSimulado[project.type] || [];
      if (!projetoOds.includes(filtroOds)) {
        return false;
      }
    }

    return true;
  });
  
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <UserHeader />
      
      {/* Título e botões de ação */}
      <div className="bg-white px-6 py-4 border-b border-gray-200">
        <div className="container mx-auto flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-800">Meus Projetos</h1>
          <div className="flex space-x-2">
            <Button 
              className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Voltar
            </Button>
            <Button 
              className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white"
              onClick={() => navigate('/dashboard')}
            >
              <Home className="h-5 w-5" />
            </Button>
            <Button 
              className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white"
              onClick={() => navigate('/all-projects')}
            >
              Todos os Projetos
            </Button>
          </div>
        </div>
      </div>
      
      {/* Tabs de Filtro */}
      <div className="bg-gray-50 px-6 border-b border-gray-200">
        <div className="container mx-auto">
          <div className="flex overflow-x-auto pb-1">
            <button
              onClick={() => handleFilterTabChange('todos')}
              className={`px-4 py-3 font-medium text-sm whitespace-nowrap transition-colors ${
                activeFilter === 'todos' 
                  ? 'text-[#0e7cb4] border-b-2 border-[#0e7cb4]' 
                  : 'text-gray-600 hover:text-[#0e7cb4]'
              }`}
            >
              Todos os Projetos
            </button>
            <button
              onClick={() => handleFilterTabChange('tipos')}
              className={`px-4 py-3 font-medium text-sm whitespace-nowrap transition-colors ${
                activeFilter === 'tipos' 
                  ? 'text-[#0e7cb4] border-b-2 border-[#0e7cb4]' 
                  : 'text-gray-600 hover:text-[#0e7cb4]'
              }`}
            >
              Tipos de Projetos
            </button>
            <button
              onClick={() => handleFilterTabChange('status')}
              className={`px-4 py-3 font-medium text-sm whitespace-nowrap transition-colors ${
                activeFilter === 'status' 
                  ? 'text-[#0e7cb4] border-b-2 border-[#0e7cb4]' 
                  : 'text-gray-600 hover:text-[#0e7cb4]'
              }`}
            >
              Status do Projetos
            </button>
            <button
              onClick={() => handleFilterTabChange('regioes')}
              className={`px-4 py-3 font-medium text-sm whitespace-nowrap transition-colors ${
                activeFilter === 'regioes' 
                  ? 'text-[#0e7cb4] border-b-2 border-[#0e7cb4]' 
                  : 'text-gray-600 hover:text-[#0e7cb4]'
              }`}
            >
              Regiões
            </button>
              <button
              onClick={() => handleFilterTabChange('ods')}
              className={`px-4 py-3 font-medium text-sm whitespace-nowrap transition-colors ${
                activeFilter === 'ods' 
                  ? 'text-[#0e7cb4] border-b-2 border-[#0e7cb4]' 
                  : 'text-gray-600 hover:text-[#0e7cb4]'
              }`}
            >
              ODS
              </button>
          </div>
          </div>
        </div>
        
      {/* Conteúdo do filtro selecionado */}
      <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
        <div className="container mx-auto">
          {activeFilter === 'todos' && (
            <div className="text-sm text-gray-700 flex justify-between items-center">
              <span>Mostrando todos os seus projetos</span>
              {(filtroTipo || filtroStatus || filtroRegiao || filtroOds) && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={limparFiltros}
                  className="text-xs"
                >
                  Limpar filtros
                </Button>
              )}
              </div>
          )}
          
          {activeFilter === 'tipos' && (
            <div className="relative">
              <button 
                onClick={() => toggleDropdown('tipos')}
                className="flex items-center justify-between w-64 text-sm bg-white px-4 py-2 rounded border border-gray-300 hover:bg-gray-50"
              >
                <span>{filtroTipo || "Selecione o tipo de projeto"}</span>
                <svg className={`w-4 h-4 transition-transform ${openDropdown === 'tipos' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
              
              {openDropdown === 'tipos' && (
                <div className="absolute z-10 mt-1 w-64 bg-white rounded-md shadow-lg border border-gray-200">
                  <ul className="py-1">
                    {filterOptions.tipos.map((tipo, index) => (
                      <li key={index}>
                        <button 
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${filtroTipo === tipo ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                          onClick={() => aplicarFiltroTipo(tipo)}
                        >
                          {tipo}
                        </button>
                      </li>
                    ))}
                    {filtroTipo && (
                      <li>
                        <button 
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 border-t border-gray-100"
                          onClick={() => setFiltroTipo(null)}
                        >
                          Limpar filtro
                        </button>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          {activeFilter === 'status' && (
            <div className="relative">
              <button 
                onClick={() => toggleDropdown('status')}
                className="flex items-center justify-between w-64 text-sm bg-white px-4 py-2 rounded border border-gray-300 hover:bg-gray-50"
              >
                <span>{filtroStatus || "Selecione o status do projeto"}</span>
                <svg className={`w-4 h-4 transition-transform ${openDropdown === 'status' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
              
              {openDropdown === 'status' && (
                <div className="absolute z-10 mt-1 w-64 bg-white rounded-md shadow-lg border border-gray-200">
                  <ul className="py-1">
                    {filterOptions.status.map((status, index) => (
                      <li key={index}>
                        <button 
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${filtroStatus === status ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                          onClick={() => aplicarFiltroStatus(status)}
                        >
                          {status}
                        </button>
                      </li>
                    ))}
                    {filtroStatus && (
                      <li>
                        <button 
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 border-t border-gray-100"
                          onClick={() => setFiltroStatus(null)}
                        >
                          Limpar filtro
                        </button>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          {activeFilter === 'regioes' && (
            <div className="relative">
              <button 
                onClick={() => toggleDropdown('regioes')}
                className="flex items-center justify-between w-64 text-sm bg-white px-4 py-2 rounded border border-gray-300 hover:bg-gray-50"
              >
                <span>{filtroRegiao || "Selecione a região"}</span>
                <svg className={`w-4 h-4 transition-transform ${openDropdown === 'regioes' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
              
              {openDropdown === 'regioes' && (
                <div className="absolute z-10 mt-1 w-64 bg-white rounded-md shadow-lg border border-gray-200">
                  <ul className="py-1">
                    {filterOptions.regioes.map((regiao, index) => (
                      <li key={index}>
                        <button 
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${filtroRegiao === regiao ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                          onClick={() => aplicarFiltroRegiao(regiao)}
                        >
                          {regiao}
                        </button>
                      </li>
                    ))}
                    {filtroRegiao && (
                      <li>
                        <button 
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 border-t border-gray-100"
                          onClick={() => setFiltroRegiao(null)}
                        >
                          Limpar filtro
                        </button>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          {activeFilter === 'ods' && (
            <div className="relative">
              <button 
                onClick={() => toggleDropdown('ods')}
                className="flex items-center justify-between w-64 text-sm bg-white px-4 py-2 rounded border border-gray-300 hover:bg-gray-50"
              >
                <span>{filtroOds || "Selecione os Objetivos de Desenvolvimento Sustentável"}</span>
                <svg className={`w-4 h-4 transition-transform ${openDropdown === 'ods' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
              
              {openDropdown === 'ods' && (
                <div className="absolute z-10 mt-1 w-64 bg-white rounded-md shadow-lg border border-gray-200">
                  <ul className="py-1">
                    {filterOptions.ods.map((ods, index) => (
                      <li key={index}>
                        <button 
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${filtroOds === ods ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                          onClick={() => aplicarFiltroOds(ods)}
                        >
                          {ods}
                        </button>
                      </li>
                    ))}
                    {filtroOds && (
                      <li>
                        <button 
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 border-t border-gray-100"
                          onClick={() => setFiltroOds(null)}
                        >
                          Limpar filtro
                        </button>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Lista de projetos */}
      <div className="flex-grow p-6 bg-gray-50">
        <div className="container mx-auto">
          {/* Status dos filtros ativos */}
          {(filtroTipo || filtroStatus || filtroRegiao || filtroOds) && (
            <div className="mb-4 flex flex-wrap gap-2 items-center">
              <span className="text-sm text-gray-600">Filtros ativos:</span>
              
              {filtroTipo && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Tipo: {filtroTipo}
                  <button onClick={() => setFiltroTipo(null)} className="ml-1 text-blue-600 hover:text-blue-800">
                    &times;
                  </button>
                </span>
              )}
              
              {filtroStatus && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Status: {filtroStatus}
                  <button onClick={() => setFiltroStatus(null)} className="ml-1 text-green-600 hover:text-green-800">
                    &times;
                  </button>
                </span>
              )}
              
              {filtroRegiao && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  Região: {filtroRegiao}
                  <button onClick={() => setFiltroRegiao(null)} className="ml-1 text-yellow-600 hover:text-yellow-800">
                    &times;
                  </button>
                </span>
              )}
              
              {filtroOds && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  ODS: {filtroOds}
                  <button onClick={() => setFiltroOds(null)} className="ml-1 text-purple-600 hover:text-purple-800">
                    &times;
                  </button>
                </span>
              )}
              
              <button 
                onClick={limparFiltros}
                className="text-xs text-gray-600 hover:text-gray-800 underline"
              >
                Limpar todos
              </button>
            </div>
          )}
          
          {userProjects.length === 0 || projetosFiltrados.length === 0 ? (
            <div className="text-center py-10">
              {userProjects.length === 0 ? (
                <>
                  <h3 className="text-lg font-medium text-gray-700 mb-2">Você ainda não possui projetos</h3>
                  <p className="text-gray-500 mb-6">Explore nossa lista de projetos disponíveis</p>
                  <Button 
                    className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white"
                    onClick={() => navigate('/all-projects')}
                  >
                    Ver Projetos Disponíveis
                  </Button>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-medium text-gray-700 mb-2">Nenhum projeto encontrado</h3>
                  <p className="text-gray-500 mb-6">Tente modificar os filtros para encontrar mais resultados</p>
                  <Button 
                    variant="outline"
                    onClick={limparFiltros}
                    className="text-[#0e7cb4] border-[#0e7cb4] hover:bg-[#0e7cb4] hover:text-white"
                  >
                    Limpar filtros
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projetosFiltrados.map(project => (
                <Card key={project.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="h-48 overflow-hidden bg-gray-200 flex items-center justify-center">
                    <ProjectImage src={project.image} alt={project.name} />
                  </div>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-lg font-semibold">{project.name}</h3>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        project.status === 'Em implementação' ? 'bg-blue-100 text-blue-800' :
                        project.status === 'Finalizado' ? 'bg-green-100 text-green-800' :
                        project.status === 'Planejamento' ? 'bg-yellow-100 text-yellow-800' : 
                        project.status === 'Crédito para Venda' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {project.status}
                      </span>
                    </div>
                    <div className="flex gap-2 mb-2">
                      <span className="text-xs bg-gray-100 px-2 py-1 rounded">{project.type}</span>
                      <span className="text-xs bg-gray-100 px-2 py-1 rounded">{project.region}</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">{project.description}</p>
                    <div className="flex justify-end space-x-2">
                      <Button 
                        variant="outline" 
                        className="text-[#0e7cb4] border-[#0e7cb4] hover:bg-[#0e7cb4] hover:text-white"
                        onClick={() => {
                          if (project.id === 10) {
                            navigate('/meu-projeto/10');
                          } else {
                            // Pode adicionar navegação para outros projetos aqui
                          }
                        }}
                      >
                        Ver detalhes
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Rodapé */}
      <Footer />
    </div>
  );
};

export default MyProjects; 